import React, { useEffect, useState, useRef, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ListView, DetailPanel, SearchFilter } from './components';
import {
  getWebhookNotificationHistoryStart,
  getWebhookNotificationDetailsStart,
  redeliverWebhookNotificationStart
} from 'redux/webhook/actions';
import { currentUser } from 'redux/session/selectors';
import {
  Loading,
  ToastNotification,
  DataTableSkeleton
} from 'carbon-components-react';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { CustomRightHeaderPanel } from 'layouts/components';
import { WebhookNotificationSearchParams } from 'redux/webhook/types';
import Utils from 'utilities/helper.util';
import { selectNotificationHistory } from 'redux/webhook';

const PAGE_SIZE = 10;

const filterStringReducer = (accumulator: string[], currentValue: any) => {
  const [key, value] = currentValue;

  switch (key) {
    case 'status':
      if (value.length > 0) accumulator.push(`${key}:${value.join('|')}`);
      return accumulator;
    case 'webhook':
      if (value.length > 0) accumulator.push(`${key}:${value.join('|')}`);
      return accumulator;
    case 'dateRange':
      return accumulator;
    default:
      if (value) accumulator.push(`${key}:${value}`);
      return accumulator;
  }
};

const WebHookHistory: React.FC<RouteComponentProps<{}, any, any>> = () => {
  const dispatch: any = useDispatch();
  const ref = useRef(null);

  const [isPannelExpanded, setIsPannelExpanded] = useState<boolean>(false);
  const [currentEventType, setCurrentEventType] = useState<string>('');
  const [downloadLink, setDownloadLink] = useState<string>('');
  const [downloadFileName, setDownloadFileName] = useState<string>('');
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [filterString, setFilterString] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [notificationHistoryList, setNotificationHistoryList] = useState<any>(
    []
  );
  const [selectedRowsId, setSelectedRowsId] = useState<object>({});
  const [totalSelectedRows, setTotalSelectedRows] = useState<number>(0);
  const [successAction, setSuccessAction] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState<any>({
    sortedBy: 'createdAt',
    sortedOrder: 'A'
  });
  const [isRedeliverInternal, setIsRedeliverInternal] = useState<any>(
    undefined
  );
  const [organisationId, setOrganisationId] = useState<number | undefined>();

  const user: any = useSelector(currentUser);
  const notificationHistory: any = useSelector(selectNotificationHistory);
  const searchFilter = notificationHistory?.allListFilters;
  const isRedeliver = notificationHistory?.isRedeliver;
  const sortOrderBy = notificationHistory?.allListOrderBy;
  const notificationHistoryInfo = notificationHistory?.allListInfo;
  const isLoading: boolean = notificationHistory?.isLoading;

  useEffect(() => {
    const queryParams: WebhookNotificationSearchParams = {
      fields:
        'id,webhook,isPaused,userId,uuid,message,status,lastResponseCode,processingTime,createdAt,failureCount',
      order: sortOrderBy || '-createdAt',
      limit: pageSize,
      offset: pageOffset,
      filter: filterString
    };
    !isRedeliver &&
      filterString &&
      queryParams &&
      dispatch(getWebhookNotificationHistoryStart(queryParams));
  }, [
    dispatch,
    pageSize,
    pageOffset,
    filterString,
    sortOrderBy,
    isRedeliver,
    organisationId
  ]);

  useEffect(() => {
    Object.keys(selectedRowsId).length > 0 &&
      isRedeliver !== undefined &&
      !isRedeliver &&
      !isRedeliverInternal &&
      !isLoading &&
      setSuccessAction({
        count: Object.keys(selectedRowsId).length,
        action:
          Object.keys(selectedRowsId).length > 1
            ? 'events were resubmitted '
            : 'event were resubmitted ',
        time: Utils.getNewDate().format('hh:mm:ss A')
      });
    setIsRedeliverInternal(false);
  }, [isRedeliver, selectedRowsId, isLoading, isRedeliverInternal]);

  useEffect(() => {
    setNotificationHistoryList(notificationHistory?.allList || []);
    setTotalItems(notificationHistoryInfo?.count || 0);
  }, [notificationHistory, notificationHistoryInfo]);

  useEffect(() => {
    const filterArr = Object.entries({
      ...searchFilter
    });
    setPageOffset(0);
    setCurrentPage(1);
    searchFilter &&
      setFilterString(filterArr.reduce(filterStringReducer, []).join(','));
  }, [searchFilter]);

  useEffect(() => {
    const element = document.createElement('a');
    element.href = downloadLink;
    element.download = downloadFileName;
    document.body.appendChild(element);
    if (downloadLink !== '') element.click();
  }, [downloadLink, downloadFileName]);

  useEffect(() => {
    Object.keys(selectedRowsId).length > 0 &&
      dispatch(redeliverWebhookNotificationStart(selectedRowsId));
  }, [dispatch, selectedRowsId]);

  useEffect(() => {
    setOrganisationId(user?.organisationId);
  }, [user]);

  const onViewDetail = useCallback(
    (notificationId: any, subscriptionId: any, currentType: string) => {
      setCurrentEventType(currentType);
      setIsPannelExpanded(true);
      dispatch(
        getWebhookNotificationDetailsStart({ subscriptionId, notificationId })
      );
    },
    [dispatch]
  );

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const onClickCloseButton = () => {
    setIsPannelExpanded(false);
  };

  const onDownload = (message: any, notificationId: any) => {
    const data = new Blob([JSON.stringify(message)], {
      type: 'application/json;charset=utf-8'
    });
    if (downloadLink !== '') window.URL.revokeObjectURL(downloadLink);
    setDownloadFileName(`json_payload_${notificationId}.json`);
    setDownloadLink(window.URL.createObjectURL(data));
  };

  const onRetryClick = (selectedRows: any) => {
    setSuccessAction(null);
    setIsRedeliverInternal(true);
    const retryObjects = selectedRows.map((row: any) => {
      const subscriptionId = row.cells[5].value;
      const notificationId = row.id;
      return { subscriptionId, notificationId };
    });
    setSelectedRowsId({});
    setSelectedRowsId(retryObjects);
  };

  return (
    <div
      id="WebhookHistory"
      data-testid="WebhookHistory"
      ref={ref}
      className="webhook-history"
    >
      <CustomRightHeaderPanel
        panelHeadingLabel={''}
        panelHeading={currentEventType}
        isExpanded={isPannelExpanded}
        closeButtonHandler={onClickCloseButton}
      >
        <DetailPanel onClose={onClickCloseButton} handleDownload={onDownload} />
      </CustomRightHeaderPanel>

      <FullHeaderLeftSideFullPanelLayout
        pageHeading="Web hook history"
        pageDescription="View web hooks notification history and manage failures"
        enableHeaderGlobalBar={true}
      >
        {successAction && (
          <div className="webhook-history-success-notification">
            <ToastNotification
              kind={'success'}
              iconDescription="Success"
              title={`${successAction.count} ${successAction.action}`}
              caption={successAction.time}
              timeout={5000}
            />
          </div>
        )}
        <hr className="divider"></hr>
        <SearchFilter
          totalSelectedRows={totalSelectedRows}
          isLoading={isLoading}
          mainContainerRef={ref}
        />
        <div
          id="webhook-history-body-content"
          className="webhook-history-body-content"
        >
          <>
            {notificationHistory?.length > 0 || (isLoading && !isRedeliver) ? (
              <DataTableSkeleton showHeader={false} />
            ) : (
              <div id="ListView" data-testid="ListView">
                {(!isLoading || isRedeliver) && (
                  <ListView
                    notificationHistory={notificationHistoryList}
                    data-testid="ListView"
                    handleViewDetail={onViewDetail}
                    handlePageChange={handlePageChange}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    handleRetryClick={onRetryClick}
                    setTotalSelectedRows={setTotalSelectedRows}
                    totalSelectedRows={totalSelectedRows}
                    sortOrder={sortOrder}
                    setSortOrder={setSortOrder}
                  />
                )}
              </div>
            )}
          </>
        </div>
      </FullHeaderLeftSideFullPanelLayout>
      {isLoading && <Loading withOverlay={true} />}
    </div>
  );
};
WebHookHistory.displayName = 'WebHookHistory';
export default WebHookHistory;
